import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import products, { initializeProducts } from '../data/products';
import ProductImage from '../components/ProductImage';
import './Menu.css';

function Menu() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  
  // Get search and category values from URL params
  const initialSearchTerm = queryParams.get('search') || '';
  const initialCategory = queryParams.get('category') || 'all';
  
  // State for search and category
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [activeCategory, setActiveCategory] = useState(initialCategory);
  
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    product: null
  });
  const [cartItems, setCartItems] = useState({});
  const [menuProducts, setMenuProducts] = useState(products);
  
  const categories = [
    { id: 'all', name: 'All Items' },
    { id: 'smoothies', name: 'Smoothies' },
    { id: 'juices', name: 'Juices' },
    { id: 'proteinBalls', name: 'Protein Balls' }
  ];
  
  // Load fresh products from API on component mount to get the latest data
  useEffect(() => {
    const loadProducts = async () => {
      try {
        console.log("Fetching latest products from API...");
        const latestProducts = await initializeProducts();
        setMenuProducts(latestProducts);
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };
    
    loadProducts();
  }, []);

  // Load cart data on component mount and when cart is updated
  useEffect(() => {
    const loadCartData = () => {
      console.log("Loading cart data");
      const cartData = localStorage.getItem('cart');
      if (cartData) {
        const cart = JSON.parse(cartData);
        // Create a map of product ID to quantity
        const itemMap = {};
        cart.forEach(item => {
          // Only add items with quantity > 0
          if (item.quantity > 0) {
            itemMap[item.product.id] = item.quantity;
          }
        });
        console.log("Updated cart mapping:", itemMap);
        setCartItems(itemMap);
      } else {
        // No cart data, ensure cartItems is empty
        console.log("No cart data found, emptying cartItems");
        setCartItems({});
      }
    };
    
    // Initial load
    loadCartData();
    
    // Set up event listener for cart updates
    const handleCartUpdate = () => {
      console.log("Cart update event received in Menu component");
      loadCartData();
    };
    
    window.addEventListener('cartUpdated', handleCartUpdate);
    
    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
    };
  }, []);
  
  // Update URL when search or category changes
  useEffect(() => {
    const params = new URLSearchParams();
    
    if (searchTerm) params.set('search', searchTerm);
    if (activeCategory && activeCategory !== 'all') params.set('category', activeCategory);
    
    const newUrl = `${location.pathname}?${params.toString()}`;
    navigate(newUrl, { replace: true });
  }, [searchTerm, activeCategory, location.pathname, navigate]);
  
  const updateCart = (product, newQuantity, event) => {
    if (event) {
      event.preventDefault();
    }
    
    console.log(`Updating cart for ${product.name}. New quantity: ${newQuantity}`);
    
    // Get current cart from localStorage
    const cartData = localStorage.getItem('cart');
    const cart = cartData ? JSON.parse(cartData) : [];
    
    if (newQuantity <= 0) {
      console.log("Removing item from cart");
      // Remove product from cart
      const updatedCart = cart.filter(item => item.product.id !== product.id);
      localStorage.setItem('cart', JSON.stringify(updatedCart));
      
      // Directly set the cartItems state to remove the product
      console.log("Current cartItems before deletion:", cartItems);
      const updatedCartItems = {...cartItems};
      delete updatedCartItems[product.id];
      console.log("Updated cartItems after deletion:", updatedCartItems);
      setCartItems(updatedCartItems);
      
      // Show removal notification
      setNotification({
        show: true,
        message: `${product.name} removed from your cart`,
        product: product
      });
    } else {
      // Check if product already exists in cart
      const existingItemIndex = cart.findIndex(item => item.product.id === product.id);
      
      if (existingItemIndex >= 0) {
        // Update quantity
        cart[existingItemIndex].quantity = newQuantity;
        setNotification({
          show: true,
          message: `Updated ${product.name} quantity to ${newQuantity}`,
          product: product
        });
      } else {
        // Add new product
        cart.push({ product, quantity: newQuantity });
        setNotification({
          show: true,
          message: `${product.name} added to your cart!`,
          product: product
        });
      }
      
      // Update local state with the new quantity
      setCartItems(prev => ({
        ...prev,
        [product.id]: newQuantity
      }));
    }
    
    // Save updated cart to localStorage
    localStorage.setItem('cart', JSON.stringify(cart));
    
    // Dispatch custom event for Header component to update cart count
    console.log("Dispatching cartUpdated event");
    window.dispatchEvent(new CustomEvent('cartUpdated'));
    
    // Hide notification after 3 seconds
    setTimeout(() => {
      setNotification({ show: false, message: '', product: null });
    }, 3000);
  };
  
  const addToCart = (product, event) => {
    // Add to cart with quantity 1
    updateCart(product, 1, event);
  };
  
  // Clear search
  const clearSearch = () => {
    setSearchTerm('');
    if (activeCategory === 'all') {
      navigate('/menu', { replace: true });
    } else {
      navigate(`/menu?category=${activeCategory}`, { replace: true });
    }
  };
  
  // Filter products based on search term and category
  const filteredProducts = useMemo(() => {
    // First, get all products or just the active category
    let productsToFilter = [];
    
    if (activeCategory === 'all') {
      // Combine all categories
      Object.values(menuProducts).forEach(categoryProducts => {
        productsToFilter = [...productsToFilter, ...categoryProducts];
      });
    } else {
      productsToFilter = menuProducts[activeCategory] || [];
    }
    
    // Apply search
    if (!searchTerm) {
      return productsToFilter;
    }
    
    return productsToFilter.filter(product => {
      // Text search (name, description, ingredients)
      return (
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.ingredients && product.ingredients.some(ingredient => 
          ingredient.toLowerCase().includes(searchTerm.toLowerCase())
        ))
      );
    });
  }, [menuProducts, activeCategory, searchTerm]);
  
  // Handle form submission
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    // The search is already applied when the searchTerm changes,
    // so we don't need to do anything else here
  };
  
  return (
    <div className="menu-page">
      {/* Notification toast */}
      {notification.show && (
        <div className="notification-toast">
          <div className="notification-content">
            <span className="notification-message">{notification.message}</span>
            {notification.product && 
              <span className="notification-details">
                ${notification.product.price.toFixed(2)}
              </span>
            }
          </div>
          <div className="notification-progress"></div>
        </div>
      )}
      
      <div className="menu-header">
        <h1>Our Menu</h1>
        <p>Explore our delicious and nutritious offerings made with the freshest ingredients.</p>
      </div>
      
      {/* Search bar */}
      <div className="search-container">
        <form onSubmit={handleSearchSubmit} className="search-form">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <button type="submit" className="search-button">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="search-icon">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
            </button>
            {searchTerm && (
              <button type="button" className="clear-search-button" onClick={clearSearch}>
                &times;
              </button>
            )}
          </div>
        </form>
      </div>
      
      <div className="category-tabs">
        {categories.map(category => (
          <button
            key={category.id}
            className={`category-tab ${activeCategory === category.id ? 'active' : ''}`}
            onClick={() => setActiveCategory(category.id)}
          >
            {category.name}
          </button>
        ))}
      </div>
      
      <div className="results-count">
        {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'} found
      </div>
      
      <div className="product-grid">
        {filteredProducts.length > 0 ? (
          filteredProducts.map(product => (
            <div className="product-card" key={product.id || product._id}>
              <div className="product-image">
                <ProductImage product={product} />
              </div>
              <div className="product-info">
                <h3>{product.name}</h3>
                <p className="product-description">{product.description}</p>
                <div className="product-price">${product.price.toFixed(2)}</div>
                
                {/* Display inventory status */}
                {product.inventory && (
                  <div className={`inventory-status ${!product.inventory.inStock || product.inventory.stockQuantity <= 0 ? 'out-of-stock' : 'in-stock'}`}>
                    {!product.inventory.inStock || product.inventory.stockQuantity <= 0 ? 
                      'Out of Stock' : 
                      product.inventory.stockQuantity <= product.inventory.lowStockThreshold ? 
                        `Low Stock (${product.inventory.stockQuantity} left)` : 
                        'In Stock'
                    }
                  </div>
                )}
                
                <div className="product-actions">
                  <Link to={`/product/${product.id || product._id}`} className="view-details">View Details</Link>
                  {/* Debug output to help understand what's happening */}
                  <div style={{display: 'none'}}>{product.id} in cart: {cartItems[product.id] ? 'Yes' : 'No'}</div>
                  
                  {!cartItems[product.id] ? (
                    product.inventory && (!product.inventory.inStock || product.inventory.stockQuantity <= 0) ? (
                      <span className="out-of-stock-message">Out of Stock</span>
                    ) : (
                      <a href="#" onClick={(e) => addToCart(product, e)} className="add-to-order">Add to Order</a>
                    )
                  ) : (
                    <div className="quantity-controls">
                      <button 
                        onClick={() => {
                          const currentQuantity = cartItems[product.id];
                          console.log("Decreasing quantity from:", currentQuantity);
                          
                          if (currentQuantity === 1) {
                            // Force update the cart items state immediately
                            const updatedCartItems = {...cartItems};
                            delete updatedCartItems[product.id];
                            setCartItems(updatedCartItems);
                            
                            // Then update localStorage and show notification
                            const cartData = localStorage.getItem('cart');
                            const cart = cartData ? JSON.parse(cartData) : [];
                            const updatedCart = cart.filter(item => item.product.id !== product.id);
                            localStorage.setItem('cart', JSON.stringify(updatedCart));
                            
                            setNotification({
                              show: true,
                              message: `${product.name} removed from your cart`,
                              product: product
                            });
                            
                            console.log("Dispatching cartUpdated event");
                            window.dispatchEvent(new CustomEvent('cartUpdated'));
                          } else {
                            // Normal update for quantities greater than 1
                            updateCart(product, currentQuantity - 1);
                          }
                        }}
                        className="quantity-btn"
                        aria-label="Decrease quantity"
                      >
                        -
                      </button>
                      <span className="quantity-value">{cartItems[product.id]}</span>
                      <button 
                        onClick={() => {
                          // Check if adding more would exceed available stock
                          const currentQuantity = cartItems[product.id];
                          const maxAvailable = product.inventory?.stockQuantity || Infinity;
                          
                          if (maxAvailable === Infinity || currentQuantity < maxAvailable) {
                            updateCart(product, currentQuantity + 1);
                          } else {
                            setNotification({
                              show: true,
                              message: `Sorry, only ${maxAvailable} units available`,
                              product: product
                            });
                          }
                        }}
                        className="quantity-btn"
                        aria-label="Increase quantity"
                        disabled={product.inventory && cartItems[product.id] >= product.inventory.stockQuantity}
                      >
                        +
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="no-products-message">
            <h3>No matching products found</h3>
            <p>Try adjusting your search to find what you're looking for</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Menu;